import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'

import {
  DEFAULT_SHORTCUT_ITEM_SIZE,
  NEED_HELP_SHORTCUT_ITEM_SIZE
} from 'modules/desktop/constants'
import { DESKTOP_WIDTH } from 'shared/hooks/useMediaQuery'

import { useDesktopStore } from '../../../stores'
import {
  DESKTOP_GRID_LARGE_SCREEN_CELL,
  DESKTOP_GRID_MIN_CELL,
  DESKTOP_GRID_MIN_GAP
} from '../DesktopGrid.constants'
import { CellSize } from '../types/grid.types'

export const useGridCalculation = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [fitCols, setFitCols] = useState(0)
  const [fitRows, setFitRows] = useState(0)
  const [cols, setCols] = useState(0)
  const [rows, setRows] = useState(0)
  const [isLargeScreen, setIsLargeScreen] = useState(
    window.innerWidth > DESKTOP_WIDTH
  )
  const overrideColsRef = useRef<number | null>(null)
  const overrideRowsRef = useRef<number | null>(null)
  const fitColsRef = useRef(0)
  const fitRowsRef = useRef(0)
  const colsRef = useRef(0)
  const rowsRef = useRef(0)
  const [cellSize, setCellSize] = useState<CellSize>({
    width: 40,
    height: 40,
    gapX: 8,
    gapY: 8
  })
  const [offset, setOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 })

  const setGridSize = useDesktopStore(state => state.setGridSize)
  const setStoreCellSize = useDesktopStore(state => state.setCellSize)
  const setStoreOffset = useDesktopStore(state => state.setOffset)

  useEffect(() => {
    setStoreCellSize(cellSize)
  }, [cellSize, setStoreCellSize])

  useEffect(() => {
    setStoreOffset(offset)
  }, [offset, setStoreOffset])

  // Layout effect: the first measurement must land before the first paint —
  // items place themselves with explicit gridColumn/RowStart against a
  // template of `cols` tracks, so painting with the initial cols=0 crams
  // everything into a 1-column band for a visible flash.
  useLayoutEffect(() => {
    const EPSILON = 1e-6
    const computeAxis = (
      available: number,
      minCell: number,
      maxCell: number,
      minGap: number
    ): { count: number; cellMax: number } => {
      if (available <= 0) {
        return { count: 0, cellMax: 0 }
      }

      const maxCount = Math.max(
        1,
        Math.floor((available + minGap) / (minCell + minGap))
      )

      for (let count = maxCount; count >= 1; count--) {
        const cellMaxWithMinGap = (available - minGap * (count - 1)) / count

        if (cellMaxWithMinGap + EPSILON < minCell) continue

        const cellMax = Math.min(maxCell, cellMaxWithMinGap)

        return {
          count,
          cellMax
        }
      }

      const fallbackCell = Math.max(0, Math.min(maxCell, available))

      return {
        count: fallbackCell > 0 ? 1 : 0,
        cellMax: fallbackCell
      }
    }

    const computeGap = (
      available: number,
      count: number,
      cell: number
    ): number => {
      if (count <= 1) return 0
      const raw = (available - cell * count) / (count - 1)
      return Math.max(0, raw)
    }

    let rafId: number | null = null

    const calculateGrid = () => {
      if (!containerRef.current) return
      const container = containerRef.current
      const rect = container.getBoundingClientRect()
      const clientWidth = container.clientWidth || rect.width
      const clientHeight = container.clientHeight || rect.height

      const computed = getComputedStyle(container)
      const paddingLeft = parseFloat(computed.paddingLeft || '0')
      const paddingRight = parseFloat(computed.paddingRight || '0')
      const paddingTop = parseFloat(computed.paddingTop || '0')
      const paddingBottom = parseFloat(computed.paddingBottom || '0')
      const availableWidth = Math.max(
        0,
        clientWidth - paddingLeft - paddingRight
      )
      let availableHeight = Math.max(
        0,
        clientHeight - paddingTop - paddingBottom
      )
      const viewportWidth = window.innerWidth
      const isLargeScreenFlag = viewportWidth > DESKTOP_WIDTH
      const maxCell = DESKTOP_GRID_LARGE_SCREEN_CELL
      const minCell = isLargeScreenFlag
        ? DESKTOP_GRID_LARGE_SCREEN_CELL
        : DESKTOP_GRID_MIN_CELL

      if (!isLargeScreenFlag) {
        const parent = container.parentElement
        const parentHeight =
          parent instanceof HTMLElement ? parent.clientHeight : 0
        const availableBelowViewport = Math.max(
          0,
          window.innerHeight - rect.top - paddingBottom
        )
        const fallbackHeightCandidates = [
          availableHeight,
          parentHeight - paddingTop - paddingBottom,
          availableBelowViewport
        ].filter(value => Number.isFinite(value))
        const fallbackHeight = Math.max(0, ...fallbackHeightCandidates)
        availableHeight = Math.max(availableHeight, fallbackHeight)
      }

      let horizontal = computeAxis(
        availableWidth,
        minCell,
        maxCell,
        DESKTOP_GRID_MIN_GAP
      )
      let vertical = computeAxis(
        availableHeight,
        minCell,
        maxCell,
        DESKTOP_GRID_MIN_GAP
      )

      if (horizontal.count === 0 && availableWidth <= 0) {
        horizontal = {
          count: Math.max(1, NEED_HELP_SHORTCUT_ITEM_SIZE.width),
          cellMax: minCell
        }
      }

      if (vertical.count === 0 && availableHeight <= 0) {
        vertical = {
          count: Math.max(1, NEED_HELP_SHORTCUT_ITEM_SIZE.height),
          cellMax: minCell
        }
      }

      const minColsRequired = Math.max(
        DEFAULT_SHORTCUT_ITEM_SIZE.width,
        NEED_HELP_SHORTCUT_ITEM_SIZE.width
      )
      const minRowsRequired = Math.max(
        DEFAULT_SHORTCUT_ITEM_SIZE.height,
        NEED_HELP_SHORTCUT_ITEM_SIZE.height
      )

      if (horizontal.count === 0 || vertical.count === 0) {
        overrideColsRef.current = null
        overrideRowsRef.current = null
        fitColsRef.current = 0
        fitRowsRef.current = 0
        colsRef.current = 0
        rowsRef.current = 0
        setFitRows(0)
        setFitCols(0)
        setCols(0)
        setRows(0)
        setGridSize(0, 0)
        setCellSize({ width: 0, height: 0, gapX: 0, gapY: 0 })
        setOffset({ x: paddingLeft, y: paddingTop })
        setIsLargeScreen(false)
        return
      }

      const maxSquareCell = Math.min(horizontal.cellMax, vertical.cellMax)
      let squareCell = maxSquareCell

      if (isLargeScreenFlag) {
        const desired = DESKTOP_GRID_LARGE_SCREEN_CELL
        if (
          horizontal.cellMax >= desired - EPSILON &&
          vertical.cellMax >= desired - EPSILON
        ) {
          squareCell = desired
        }
      } else {
        if (maxSquareCell < DESKTOP_GRID_MIN_CELL) {
          squareCell = maxSquareCell
        } else {
          const cappedByLarge = Math.min(
            maxSquareCell,
            DESKTOP_GRID_LARGE_SCREEN_CELL
          )
          squareCell = Math.max(DESKTOP_GRID_MIN_CELL, cappedByLarge)
        }
      }

      squareCell = Math.max(0, squareCell)

      const gapX = computeGap(availableWidth, horizontal.count, squareCell)
      const gapY = computeGap(availableHeight, vertical.count, squareCell)

      setIsLargeScreen(isLargeScreenFlag)
      setFitCols(horizontal.count)
      fitColsRef.current = horizontal.count
      setFitRows(vertical.count)
      fitRowsRef.current = vertical.count

      const override = overrideColsRef.current
      const baseEffectiveCols =
        override != null
          ? Math.max(horizontal.count, override)
          : horizontal.count
      const effectiveCols = Math.max(baseEffectiveCols, minColsRequired)
      const overrideRows = overrideRowsRef.current
      const baseEffectiveRows =
        overrideRows != null
          ? Math.max(vertical.count, overrideRows)
          : vertical.count
      const effectiveRows = Math.max(baseEffectiveRows, minRowsRequired)

      setCols(effectiveCols)
      colsRef.current = effectiveCols
      setRows(effectiveRows)
      rowsRef.current = effectiveRows
      setGridSize(effectiveCols, effectiveRows)
      setCellSize({
        width: squareCell,
        height: squareCell,
        gapX,
        gapY
      })
      setOffset({ x: paddingLeft, y: paddingTop })
    }
    calculateGrid()

    // Throttle resize callbacks to once per animation frame
    const scheduleCalculation = () => {
      if (rafId != null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        calculateGrid()
      })
    }

    const resizeObserver = new ResizeObserver(scheduleCalculation)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }
    window.addEventListener('resize', scheduleCalculation)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', scheduleCalculation)
      if (rafId != null) {
        cancelAnimationFrame(rafId)
        rafId = null
      }
    }
  }, [setGridSize])

  const setRequiredCols = useCallback(
    (required: number | null) => {
      const normalized =
        required != null && required > 0
          ? Math.max(required, fitColsRef.current)
          : null

      if (normalized === overrideColsRef.current) {
        return
      }

      overrideColsRef.current = normalized

      const baseCols = normalized != null ? normalized : fitColsRef.current
      const safeCols = Math.max(0, baseCols)
      colsRef.current = safeCols
      setCols(safeCols)

      const currentRows = rowsRef.current
      setGridSize(safeCols, currentRows)
    },
    [setGridSize]
  )

  const setRequiredRows = useCallback(
    (required: number | null) => {
      const normalized =
        required != null && required > 0
          ? Math.max(required, fitRowsRef.current)
          : null

      if (normalized === overrideRowsRef.current) {
        return
      }

      overrideRowsRef.current = normalized

      const baseRows = normalized != null ? normalized : fitRowsRef.current
      const safeRows = Math.max(0, baseRows)
      rowsRef.current = safeRows
      setRows(safeRows)

      const currentCols = colsRef.current
      setGridSize(currentCols, safeRows)
    },
    [setGridSize]
  )

  return {
    containerRef,
    cols,
    rows,
    fitCols,
    fitRows,
    cellSize,
    offset,
    setRequiredCols,
    setRequiredRows,
    isLargeScreen
  }
}
